import { Track } from "@/src/lib/tracking";
import { PendingRequest, SendOptions } from "./types";

/**
 * 请求-响应管理器
 * 负责管理待响应的请求，处理超时和响应匹配
 */
export class RequestResponseManager {
  private pendingRequests: Map<string, PendingRequest> = new Map();
  private requestIdCounter: number = 0;
  private debug: boolean;

  constructor(debug: boolean = false) {
    this.debug = debug;
  }

  /**
   * 生成唯一的请求 ID
   */
  generateRequestId(): string {
    this.requestIdCounter++;
    return `req_${Date.now()}_${this.requestIdCounter}`;
  }

  /**
   * 注册一个待响应的请求
   * @param message 消息内容
   * @param options 发送选项
   * @returns 请求 ID
   */
  registerRequest(message: string, options: SendOptions): string {
    const requestId = this.generateRequestId();
    const timeout = options.timeout ?? 5000;

    const request: PendingRequest = {
      id: requestId,
      message,
      options,
      sentAt: Date.now(),
    };

    // 设置超时定时器
    if (options.expectResponse && timeout > 0) {
      request.timeoutTimer = setTimeout(() => {
        this.handleTimeout(requestId);
      }, timeout);
    }

    this.pendingRequests.set(requestId, request);

    if (this.debug) {
    }

    return requestId;
  }

  /**
   * 处理请求超时
   */
  private handleTimeout(requestId: string): void {
    const request = this.pendingRequests.get(requestId);
    if (!request) {
      return;
    }

    Track.log(
      "warn",
      "RequestResponseManager",
      `请求 ${requestId} 超时 (${Date.now() - request.sentAt}ms)`,
    );

    // 调用错误回调
    if (request.options.onError) {
      const error = new Error(`请求超时: ${requestId}`);
      request.options.onError(error);
    }

    // 移除请求
    this.removeRequest(requestId);
  }

  /**
   * 尝试匹配响应消息
   * @param response 响应数据
   * @returns 是否匹配成功
   */
  matchResponse(response: any): boolean {
    let matched = false;

    for (const [requestId, request] of this.pendingRequests.entries()) {
      // 跳过不期望响应的请求
      if (!request.options.expectResponse) {
        continue;
      }

      // 使用自定义验证器
      if (request.options.responseValidator) {
        if (request.options.responseValidator(response, requestId)) {
          this.handleResponse(requestId, response);
          matched = true;
          break;
        }
      }
      // 默认匹配逻辑：检查响应中是否包含 requestId
      else if (this.defaultResponseMatcher(response, requestId)) {
        this.handleResponse(requestId, response);
        matched = true;
        break;
      }
    }

    return matched;
  }

  /**
   * 默认的响应匹配器
   * 检查响应对象中是否包含请求 ID
   */
  private defaultResponseMatcher(response: any, requestId: string): boolean {
    if (!response || typeof response !== "object") {
      return false;
    }

    // 检查常见的 ID 字段
    return (
      response.requestId === requestId ||
      response.request_id === requestId ||
      response.id === requestId ||
      response.msgId === requestId ||
      response.msg_id === requestId
    );
  }

  /**
   * 处理收到的响应
   */
  private handleResponse(requestId: string, response: any): void {
    const request = this.pendingRequests.get(requestId);
    if (!request) {
      return;
    }

    if (this.debug) {
    }

    // 清除超时定时器
    if (request.timeoutTimer) {
      clearTimeout(request.timeoutTimer);
    }

    // 调用响应回调
    if (request.options.onResponse) {
      try {
        request.options.onResponse(response);
      } catch (error) {
        Track.log(
          "error",
          "RequestResponseManager",
          `响应回调执行出错 (${requestId}):`,
          { cause: String(error) },
        );
      }
    }

    // 移除请求
    this.removeRequest(requestId);
  }

  /**
   * 移除请求
   */
  removeRequest(requestId: string): void {
    const request = this.pendingRequests.get(requestId);
    if (request && request.timeoutTimer) {
      clearTimeout(request.timeoutTimer);
    }
    this.pendingRequests.delete(requestId);
  }

  /**
   * 清除所有待处理的请求
   */
  clear(): void {
    // 清除所有超时定时器
    for (const request of this.pendingRequests.values()) {
      if (request.timeoutTimer) {
        clearTimeout(request.timeoutTimer);
      }
    }

    this.pendingRequests.clear();

    if (this.debug) {
    }
  }

  /**
   * 获取待处理请求的数量
   */
  getPendingCount(): number {
    return this.pendingRequests.size;
  }

  /**
   * 获取所有待处理请求的信息
   */
  getPendingRequests(): Array<{
    id: string;
    message: string;
    sentAt: number;
    elapsed: number;
  }> {
    const now = Date.now();
    return Array.from(this.pendingRequests.values()).map((request) => ({
      id: request.id,
      message: request.message,
      sentAt: request.sentAt,
      elapsed: now - request.sentAt,
    }));
  }

  /**
   * 重置所有待处理请求（用于重连后）
   * 可选择是否触发超时错误
   */
  reset(triggerTimeout: boolean = false): void {
    if (triggerTimeout) {
      for (const requestId of this.pendingRequests.keys()) {
        this.handleTimeout(requestId);
      }
    } else {
      this.clear();
    }
  }
}
